import {
  Card,
  Header,
  IconExternalLink,
  PrimaryButton,
  Space,
  Text,
} from '@procurify/ui'

import { Price } from '@webapp/core/components/Price'
import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  ConversationOrderSummaryDelivery,
  type IConversationOrderSummaryDelivery,
  type IConversationOrderSummaryDeliveryItem,
} from './ConversationOrderSummaryDelivery'
import { ConversationOrderSummarySource } from './ConversationOrderSummarySource'
import { ConversationOrderSummaryStepper } from './ConversationOrderSummaryStepper'
import { ConversationOrderSummaryTotals } from './ConversationOrderSummaryTotals'
import messages from './messages'
import { ConversationOrderSummaryCardStyled } from './styles/ConversationOrderSummaryCardStyled'
import { ConversationOrderSummaryValueStyled } from './styles/ConversationOrderSummaryValueStyled'
import {
  type ConversationCartAction,
  type ConversationCartAmount,
  type ConversationVendor,
} from '../../value-objects/ConversationTurnItem'
import { ConversationCartActionIcons } from '../../value-objects/enums/ConversationCartActionIconEnum'
import { VendorLogoBadgeMarkHeights } from '../VendorLogoBadge'

export interface IConversationCartOrderSummaryGroup {
  vendor?: ConversationVendor
  deliveries: IConversationOrderSummaryDelivery[]
  subtotal?: ConversationCartAmount
}

export interface IConversationCartOrderSummaryProps {
  groups: IConversationCartOrderSummaryGroup[]
  total: Nullable<number>
  currency: CurrencyName
  disabled?: boolean
  isContinueLoading?: boolean
  isRemoveDisabled?: boolean
  totalLabel?: string
  continueAction?: ConversationCartAction
  disclaimer?: string
  onQuantityChange: (itemId: string, quantity: number) => void
  onRemove: (itemId: string) => void
  onContinue: () => void
}

export const ConversationCartOrderSummary = ({
  groups,
  total,
  currency,
  disabled,
  isContinueLoading,
  isRemoveDisabled,
  totalLabel,
  continueAction,
  disclaimer,
  onQuantityChange,
  onRemove,
  onContinue,
}: IConversationCartOrderSummaryProps) => {
  const intl = useProcurifyIntl()
  const isSingleVendor = groups.length === 1
  const headingVendor = isSingleVendor ? groups[0].vendor : undefined
  const lineCount = groups.reduce(
    (count, group) =>
      group.deliveries.reduce(
        (groupCount, delivery) => groupCount + delivery.items.length,
        count
      ),
    0
  )
  const isExternalCheckout =
    continueAction?.icon === ConversationCartActionIcons.EXTERNAL

  const renderQuantity = (item: IConversationOrderSummaryDeliveryItem) => (
    <ConversationOrderSummaryStepper
      item={item}
      disabled={disabled}
      isRemoveDisabled={isRemoveDisabled}
      onQuantityChange={onQuantityChange}
      onRemove={onRemove}
    />
  )

  const renderGroup = (group: IConversationCartOrderSummaryGroup) =>
    group.deliveries.map((delivery, index) => (
      <ConversationOrderSummaryDelivery
        key={delivery.id}
        delivery={delivery}
        position={index + 1}
        currency={currency}
        isCollapsible={false}
        isLineCentered
        renderQuantity={renderQuantity}
      />
    ))

  const renderVendorSubtotal = (
    group: IConversationCartOrderSummaryGroup,
    index: number
  ) =>
    group.subtotal ? (
      <Space
        key={group.vendor?.id ?? index}
        direction='horizontal'
        justify='space-between'
        align='center'
      >
        <Text inline bold>
          {intl.formatMessage(messages.vendorSubtotal, {
            vendor: group.vendor?.name ?? '',
          })}
        </Text>
        <ConversationOrderSummaryValueStyled $isBold className='fs-mask'>
          <Price
            inline
            price={group.subtotal.price}
            currency={group.subtotal.currency}
          />
        </ConversationOrderSummaryValueStyled>
      </Space>
    ) : null

  const renderTotals = () =>
    total === null ? (
      groups.map(renderVendorSubtotal)
    ) : (
      <ConversationOrderSummaryTotals
        total={total}
        currency={currency}
        totalLabel={totalLabel ?? intl.formatMessage(messages.estimatedTotal)}
      />
    )

  return (
    <ConversationOrderSummaryCardStyled>
      <Card.Content>
        <Space direction='vertical' size='medium'>
          <Space direction='horizontal' size='small' align='end'>
            <Header as='h5'>
              {intl.formatMessage(messages.heading, { count: lineCount })}
            </Header>
            {headingVendor && (
              <ConversationOrderSummarySource
                vendor={headingVendor}
                outlined={false}
                markHeight={VendorLogoBadgeMarkHeights.COMPACT}
              />
            )}
          </Space>

          {groups.map((group, index) =>
            isSingleVendor ? (
              renderGroup(group)
            ) : (
              <Space
                key={group.vendor?.id ?? index}
                direction='vertical'
                size='medium'
              >
                {group.vendor && (
                  <ConversationOrderSummarySource
                    vendor={group.vendor}
                    outlined={false}
                  />
                )}
                {renderGroup(group)}
              </Space>
            )
          )}

          <Space direction='vertical' size='xsmall'>
            {renderTotals()}
            {disclaimer && <Text color='textSecondary'>{disclaimer}</Text>}
          </Space>

          <PrimaryButton
            analyticsId='conversation-review-order-button'
            showIconOnEnd={isExternalCheckout}
            iconComponent={
              isExternalCheckout ? <IconExternalLink /> : undefined
            }
            disabled={disabled}
            isLoading={isContinueLoading}
            onClick={onContinue}
          >
            {continueAction?.text ??
              intl.formatMessage(messages.continueButton)}
          </PrimaryButton>
        </Space>
      </Card.Content>
    </ConversationOrderSummaryCardStyled>
  )
}
